import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import GlobalFilterBar from '../../components/ui/GlobalFilterBar';
import RealTimeStatusIndicator from '../../components/ui/RealTimeStatusIndicator';
import GlobalControls from './components/GlobalControls';
import PerformanceCard from './components/PerformanceCard';
import PerformanceHeatmap from './components/PerformanceHeatmap';
import AnalyticsTable from './components/AnalyticsTable';
import AdvancedFilterSidebar from './components/AdvancedFilterSidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';

const CompanyPerformanceAnalyticsDashboard = () => {
  const [selectedDateRange, setSelectedDateRange] = useState('last-30-days');
  const [selectedMetric, setSelectedMetric] = useState('sales');
  const [comparisonMode, setComparisonMode] = useState('absolute');
  const [expandedCards, setExpandedCards] = useState(new Set());
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});

  // Mock company data
  const [companies] = useState([
    {
      id: 1,
      name: "Acme Corporation",
      region: "North America",
      totalSales: 2450000,
      totalCollections: 2205000,
      salesGrowth: 15.2,
      collectionRate: 90.0,
      targetAchievement: 122.5,
      outstanding: 245000,
      avgDealSize: 45000,
      activeDeals: 28,
      lastUpdated: "2 min ago",
      weeklyTrend: [2, -1, 3, 1, -2, 4, 2]
    },
    {
      id: 2,
      name: "Tech Solutions Inc.",
      region: "North America",
      totalSales: 1890000,
      totalCollections: 1701000,
      salesGrowth: 8.7,
      collectionRate: 90.0,
      targetAchievement: 94.5,
      outstanding: 189000,
      avgDealSize: 32000,
      activeDeals: 22,
      lastUpdated: "5 min ago",
      weeklyTrend: [1, 2, -1, 2, 1, -1, 3]
    },
    {
      id: 3,
      name: "Global Enterprises",
      region: "Europe",
      totalSales: 3200000,
      totalCollections: 2880000,
      salesGrowth: 22.1,
      collectionRate: 90.0,
      targetAchievement: 160.0,
      outstanding: 320000,
      avgDealSize: 67000,
      activeDeals: 35,
      lastUpdated: "1 min ago",
      weeklyTrend: [3, 2, 4, 1, 3, 2, 4]
    },
    {
      id: 4,
      name: "Innovation Labs",
      region: "Asia Pacific",
      totalSales: 1560000,
      totalCollections: 1404000,
      salesGrowth: -3.2,
      collectionRate: 90.0,
      targetAchievement: 78.0,
      outstanding: 156000,
      avgDealSize: 28000,
      activeDeals: 18,
      lastUpdated: "8 min ago",
      weeklyTrend: [-1, -2, 1, -1, 0, -1, 1]
    },
    {
      id: 5,
      name: "Future Systems",
      region: "Europe",
      totalSales: 2100000,
      totalCollections: 1890000,
      salesGrowth: 12.5,
      collectionRate: 90.0,
      targetAchievement: 105.0,
      outstanding: 210000,
      avgDealSize: 38000,
      activeDeals: 25,
      lastUpdated: "3 min ago",
      weeklyTrend: [2, 1, 2, 3, 1, 2, 1]
    },
    {
      id: 6,
      name: "Digital Dynamics",
      region: "Asia Pacific",
      totalSales: 1780000,
      totalCollections: 1602000,
      salesGrowth: 6.8,
      collectionRate: 90.0,
      targetAchievement: 89.0,
      outstanding: 178000,
      avgDealSize: 31000,
      activeDeals: 21,
      lastUpdated: "6 min ago",
      weeklyTrend: [1, 0, 2, 1, 1, 0, 2]
    },
    {
      id: 7,
      name: "Quantum Corp",
      region: "North America",
      totalSales: 2890000,
      totalCollections: 2601000,
      salesGrowth: 18.9,
      collectionRate: 90.0,
      targetAchievement: 144.5,
      outstanding: 289000,
      avgDealSize: 52000,
      activeDeals: 31,
      lastUpdated: "4 min ago",
      weeklyTrend: [3, 2, 3, 4, 2, 3, 2]
    },
    {
      id: 8,
      name: "Alpha Industries",
      region: "Latin America",
      totalSales: 1340000,
      totalCollections: 1206000,
      salesGrowth: -1.5,
      collectionRate: 90.0,
      targetAchievement: 67.0,
      outstanding: 134000,
      avgDealSize: 24000,
      activeDeals: 16,
      lastUpdated: "7 min ago",
      weeklyTrend: [-1, 0, -1, 1, -1, 0, -1]
    },
    {
      id: 9,
      name: "Beta Solutions",
      region: "Middle East & Africa",
      totalSales: 1920000,
      totalCollections: 1728000,
      salesGrowth: 9.3,
      collectionRate: 90.0,
      targetAchievement: 96.0,
      outstanding: 192000,
      avgDealSize: 35000,
      activeDeals: 23,
      lastUpdated: "9 min ago",
      weeklyTrend: [1, 2, 1, 1, 2, 1, 2]
    },
    {
      id: 10,
      name: "Gamma Technologies",
      region: "Europe",
      totalSales: 2650000,
      totalCollections: 2385000,
      salesGrowth: 16.7,
      collectionRate: 90.0,
      targetAchievement: 132.5,
      outstanding: 265000,
      avgDealSize: 48000,
      activeDeals: 29,
      lastUpdated: "2 min ago",
      weeklyTrend: [2, 3, 2, 3, 2, 4, 3]
    },
    {
      id: 11,
      name: "Delta Enterprises",
      region: "Asia Pacific",
      totalSales: 1450000,
      totalCollections: 1305000,
      salesGrowth: 4.2,
      collectionRate: 90.0,
      targetAchievement: 72.5,
      outstanding: 145000,
      avgDealSize: 26000,
      activeDeals: 17,
      lastUpdated: "11 min ago",
      weeklyTrend: [0, 1, 0, 1, 1, 0, 1]
    },
    {
      id: 12,
      name: "Epsilon Group",
      region: "Latin America",
      totalSales: 1680000,
      totalCollections: 1512000,
      salesGrowth: 7.1,
      collectionRate: 90.0,
      targetAchievement: 84.0,
      outstanding: 168000,
      avgDealSize: 29000,
      activeDeals: 19,
      lastUpdated: "5 min ago",
      weeklyTrend: [1, 1, 2, 0, 1, 1, 2]
    }
  ]);

  const handleCardExpand = (companyId) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(companyId)) {
        newSet.delete(companyId);
      } else {
        newSet.add(companyId);
      }
      return newSet;
    });
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API call
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1500);
  };

  const handleApplyFilters = (filters) => {
    setAppliedFilters(filters);
    // In a real app, this would filter the companies data
    console.log('Applied filters:', filters);
  };

  // Listen for global filter changes
  useEffect(() => {
    const handleFiltersChanged = (event) => {
      console.log('Global filters changed:', event.detail);
    };

    window.addEventListener('filtersChanged', handleFiltersChanged);
    return () => window.removeEventListener('filtersChanged', handleFiltersChanged);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <GlobalFilterBar />
      
      <div className="flex">
        {/* Main Content */}
        <div className={`flex-1 transition-all duration-300 ${isFilterSidebarOpen ? 'lg:mr-80' : ''}`}>
          <div className="p-4 lg:p-6">
            {/* Page Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Company Performance Analytics</h1>
                <p className="text-sm text-muted-foreground mt-1">
                  Detailed comparative analysis and benchmarking across individual companies
                </p>
              </div>
              <div className="flex items-center space-x-3">
                <RealTimeStatusIndicator />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsFilterSidebarOpen(!isFilterSidebarOpen)}
                >
                  <Icon name="Filter" size={14} />
                  Advanced Filters
                </Button>
              </div>
            </div>

            {/* Global Controls */}
            <GlobalControls
              selectedDateRange={selectedDateRange}
              onDateRangeChange={setSelectedDateRange}
              selectedMetric={selectedMetric}
              onMetricChange={setSelectedMetric}
              comparisonMode={comparisonMode}
              onComparisonModeChange={setComparisonMode}
              onRefresh={handleRefresh}
              isRefreshing={isRefreshing}
            />

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6 mb-6">
              {/* Performance Cards Grid */}
              <div className="xl:col-span-3">
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  {companies.slice(0, 9).map((company) => (
                    <PerformanceCard
                      key={company.id}
                      company={company}
                      onExpand={handleCardExpand}
                      isExpanded={expandedCards.has(company.id)}
                    />
                  ))}
                </div>
              </div>

              {/* Performance Heatmap */}
              <div className="xl:col-span-1">
                <PerformanceHeatmap
                  companies={companies}
                  selectedMetric={selectedMetric}
                />
              </div>
            </div>

            {/* Analytics Table */}
            <AnalyticsTable
              companies={companies}
              selectedMetric={selectedMetric}
            />
          </div>
        </div>

        {/* Advanced Filter Sidebar */}
        <AdvancedFilterSidebar
          isOpen={isFilterSidebarOpen}
          onClose={() => setIsFilterSidebarOpen(false)}
          onApplyFilters={handleApplyFilters}
          currentFilters={appliedFilters}
        />
      </div>
    </div>
  );
};

export default CompanyPerformanceAnalyticsDashboard;